// components/MultiPlatformPixelScript/utils.ts — Session dedup keys + retry helper
import type { FireContext, PlatformPixelConfig } from './types';

export const getSessionKey = (platform: string, eventName: string, pageType: string, orderId?: string, contentName?: string): string => {
    if (pageType === 'thankYouPage' && orderId) {
        return `pixel_fired_${platform}_${eventName}_${pageType}_${orderId}`;
    }
    const safeContentName = contentName ? contentName.replace(/[^a-zA-Z0-9]/g, '_') : 'unknown';
    return `pixel_fired_${platform}_${eventName}_${pageType}_${safeContentName}`;
};

export const checkAndMarkEvent = (key: string): boolean => {
    try {
        if (sessionStorage.getItem(key) === 'true') {
            return false;
        }
        sessionStorage.setItem(key, 'true');
        return true;
    } catch {
        return true;
    }
};

export const createFireWithRetry = (ctx: {
    logPrefix: string;
    pageType: FireContext['pageType'];
    orderData?: FireContext['orderData'];
    contentName?: string;
    configs: PlatformPixelConfig[];
    hasFiredRef: FireContext['hasFiredRef'];
    timeoutIdsRef: FireContext['timeoutIdsRef'];
}): FireContext['fireWithRetry'] => {
    const { logPrefix, pageType, orderData, contentName, configs, hasFiredRef, timeoutIdsRef } = ctx;

    return (
        platform: string,
        eventName: string,
        checkFn: () => boolean,
        fireFn: () => void,
        maxRetries: number = 12
    ) => {
        let attempt = 0;
        const sessionKey = getSessionKey(platform, eventName, pageType, orderData?.orderId, contentName);

        const tryFire = () => {
            attempt++;
            const fireKey = `${platform}-${pageType}-${eventName}-${configs.map(c => c.pixelIds.join(',')).join('_')}`;

            if (hasFiredRef.current.has(fireKey)) {
                console.log(`[${logPrefix} ${platform}] Already fired for this component mount, skipping`);
                return;
            }

            if (checkFn()) {
                console.log(`[${logPrefix} ${platform}] SDK found on attempt ${attempt}!`);

                if (pageType === 'thankYouPage' && sessionKey) {
                    if (!checkAndMarkEvent(sessionKey)) {
                        console.log(`[${logPrefix} ${platform}] EVENT ALREADY FIRED THIS SESSION: ${eventName}, skipping`);
                        return;
                    }
                }

                fireFn();
                hasFiredRef.current.add(fireKey);
                console.log(`[${logPrefix} ${platform}] Event "${eventName}" fired`);
            } else if (attempt < maxRetries) {
                const delay = attempt === 1 ? 100 : 500 * attempt;
                console.log(`[${logPrefix} ${platform}] SDK not ready (attempt ${attempt}/${maxRetries}), retry in ${delay}ms`);
                timeoutIdsRef.current.push(setTimeout(tryFire, delay));
            } else {
                console.error(`[${logPrefix} ${platform}] FAILED: SDK not found after ${maxRetries} attempts`);
            }
        };

        const initialTimeoutId = setTimeout(() => {
            tryFire();
        }, 300);
        timeoutIdsRef.current.push(initialTimeoutId);
    };
};
